import _ from "lodash";
import React, { useCallback, useEffect, useRef, useState } from "react";
import withDerivedProps from "lib/withDerivedProps";

// Components
import I18nHOC from "UIComponents/I18nHOC";
import UIModal from "UIComponents/UIModal";
import Accordian from "UIComponents/Accordian";
import CriteriaRating from "UIComponents/CriteriaRating";
import Loading from "UIComponents/Loading";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import withLoader from "UIComponents/GqlWithLoader";
import SkeletonLoader from "UIComponents/SkeletonLoader";

// Graphql
import {
  getCourseSubjectFlatVariantsLevelQuery,
  getLearningCourseSubjectFlatVariantsLevelQuery,
} from "ClassRoom/modules/ClassRoomQuery";
import { getSubjectFlatVariantsLevelFromCache } from "ClassRoom/modules/ClassRoomGraphqlHelpers";
import {
  getSubjectCategoryGradeScaleQuery,
  getSubjectTermGradeScaleQuery,
} from "Teacher/routes/LearningCourse/routes/CourseSettings/routes/GradingConfigurations/modules/GradingConfigurationQueries";

// Modules
import { createAssessmentTool } from "AssessmentToolSetup/modules/AssessmentToolSetupModule";
import {
  updateCategoryGradeBoundary,
  getScoreDetailsMemoize,
} from "ScoreCategories/modules/ScoreCategoriesModule";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { removeAssessmentTool } from "Assessments/modules/AssessmentModule";
import { updateQueryParameters } from "modules/NavigationModule";
import { getSelectedAcademicYearId } from "modules/CommonSelectors";

// Design system
import {
  Checkbox,
  Table,
  TextInput,
  Tooltip,
  Button,
  IconButton,
  Dropdown,
  DropdownMenu,
  ButtonDisclosure,
} from "@toddle-edu/ds-web";
import {
  CloseOutlined,
  ChevronRightOutlined,
  InformationOutlined,
} from "@toddle-edu/ds-icons";

// third party libraries
import { compose, graphql } from "lib/apolloClient";
import classNames from "classnames";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import { Popover, ArrowContainer } from "@toddle-edu/react-tiny-popover";

// utils
import { getCategoryGradeBoundaryValues } from "ScoreCategories/modules/Utils";
import {
  getCategoriesForGradeBoundaryTool,
  getIsIBGradeScaleTool,
  getAutoEvaluateIBGradeToolTypeName,
} from "ClassRoom/modules/utils";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { getSelectedLearningCourseId } from "LearningCourse/modules/Utils";
import { getIsLearningCoursesEnabled } from "LearningCourses/modules/Utils";
import { getResolvedMinimalTreeSet } from "../../../Assessments/modules/Utils";
import { getGradingSetupConfigurations } from "GradingSetupV2/modules/Utils";

// Constants
import {
  ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY,
} from "Constants/stringConstants";
import { colors, DOUBLE_DIGIT_DECIMAL_NUMBERS_REGEX } from "Constants";

// Reuse parent component's scss since structure is identical
import classes from "../AutomaticIBScaleTool/AutomaticIBScaleTool.scss";

const styles = {
  textInputStyles: {
    width: "76px",
  },
  criteriaRatingContainerStyle: {
    width: "max-content",
  },
  leftArrowStyle: {
    alignItems: "flex-start",
    width: "24px",
    height: "24px",
  },
  headerStyle: {
    padding: 0,
  },
  itemTitleContainerStyle: {
    maxWidth: "352px",
  },
  popoverContainerStyle: {
    zIndex: 2500,
  },
  arrowStyle: {
    opacity: 0.7,
  },
};

const IB_GRADE_SCALE = [1, 2, 3, 4, 5, 6, 7];
const IB_TOK_GRADE_SCALE = ["E", "D", "C", "B", "A"];

/**
 * V2: Creates the empty grade boundary template rows dynamically based on
 * levelTags (any number of levels, any label). Falls back to ["SL"] when no
 * levels are selected yet so the table always renders with at least one column.
 */
const getGradeBoundaryInitialValues = ({ subjectType, levelTags = [] }) => {
  const isTOKSubject = _.isEqual(subjectType, "TOK");
  const gradeScale = isTOKSubject ? IB_TOK_GRADE_SCALE : IB_GRADE_SCALE;
  const firstGrade = isTOKSubject ? "E" : 1;
  const levelKeys = _.isEmpty(levelTags) ? ["SL"] : _.map(levelTags, "label");

  return _.map(gradeScale, grade => {
    const isFirstGrade = _.isEqual(grade, firstGrade);
    const levelValues = _.reduce(
      levelKeys,
      (acc, label) => {
        acc[label] = {
          minValue: isFirstGrade ? "0" : "",
          maxValue: "",
        };
        return acc;
      },
      {}
    );
    return { id: grade, grade, ...levelValues };
  });
};

const getShouldCreateNewTool = params => {
  const { gradeBoundaryMethod, assessmentToolType } = params;

  if (_.isEmpty(assessmentToolType)) return false;

  let prevSelectedMethod = "";

  switch (assessmentToolType) {
    case ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY:
      prevSelectedMethod = "category_boundary";
      break;
    case ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY:
      prevSelectedMethod = "term_boundary";
      break;
    default:
      prevSelectedMethod = "custom_boundary";
  }

  return !_.isEqual(prevSelectedMethod, gradeBoundaryMethod);
};

const getGradeBoundaryOptions = ({
  t,
  hasCategoryGradeBoundary,
  hasTermGradeBoundary,
  categoryId,
  assessmentToolType,
}) => {
  return [
    {
      key: "category_boundary",
      label: t("classRoom:use_category_grade_boundary"),
      subText: t("classRoom:use_category_grade_boundary_subtext"),
      disabled:
        !hasCategoryGradeBoundary &&
        assessmentToolType !== ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY,
      disabledTooltip: categoryId
        ? t("classRoom:no_category_linked_with_assessment")
        : t("classRoom:assign_category_to_task"),
    },
    {
      key: "term_boundary",
      label: t("classRoom:use_term_grade_boundary"),
      subText: t("classRoom:use_term_grade_boundary_subtext"),
      disabled:
        !hasTermGradeBoundary &&
        assessmentToolType !== ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY,
      disabledTooltip: t("classRoom:no_term_configured_for_subject"),
    },
    {
      key: "custom_boundary",
      label: t("classRoom:define_custom_grade_boundary"),
      subText: t("classRoom:define_custom_grade_boundary_subtext"),
    },
  ];
};

/**
 * Filters a boundary object's categories to only include values whose
 * genericTag.id is in the selected subjectLevelGenericTags. This ensures
 * category/term boundaries only show data for levels tagged on the assignment.
 */
const getFilteredGradeBoundaries = ({
  gradeBoundary,
  subjectLevelGenericTags,
  scoreKey = "values",
}) => {
  const categories = _.get(gradeBoundary, "categories", []);

  // V2: When no levels are tagged on the assignment (noLevels=true), the grade
  // scale may still have level-specific scoreValues (e.g. "CL", "FL"). We take
  // the first scoreValue per grade and normalize its genericTag.label to "SL" so
  // getCategoryGradeBoundaryValues stores it under the "SL" key — which is the
  // same key the table looks up via firstLevelLabel when noLevels=true.
  if (_.isEmpty(subjectLevelGenericTags)) {
    return {
      ...gradeBoundary,
      categories: _.map(categories, category => {
        const values = _.get(category, `${scoreKey}`, []);
        const firstValue = _.first(values);
        if (!firstValue) return category;
        return {
          ...category,
          [`${scoreKey}`]: [
            {
              ...firstValue,
              genericTag: {
                ..._.get(firstValue, "genericTag", {}),
                label: "SL",
              },
            },
          ],
        };
      }),
    };
  }

  const tags = _.uniq(_.map(subjectLevelGenericTags, tag => _.get(tag, "id")));

  return {
    ...gradeBoundary,
    categories: _.map(categories, category => {
      const values = _.get(category, `${scoreKey}`, []);
      return {
        ...category,
        [`${scoreKey}`]: _.filter(values, value => {
          const genericTagId = _.get(value, "genericTag.id", null);
          return _.includes(tags, genericTagId);
        }),
      };
    }),
  };
};

/**
 * V2: Renders a single table cell.
 * - GRADE_SCALE: returns grade value text.
 * - SCORE_RANGE_${label}: renders inputs/text for that specific level.
 * - SAME_SCALE / SCORE_RANGE: uses firstLevelLabel (when same-scale or no-levels fallback).
 */
const getTableRowValue = params => {
  const {
    t,
    header,
    boundary,
    onChangeInput,
    isFirstRow,
    isLastRow,
    mode,
    isEditable,
    errors = {},
    autoFocusKey,
    setAutoFocusKey,
    index,
    disableTextInput,
    errorsCount,
    showModalInViewModal,
    firstLevelLabel = "SL",
  } = params;
  const { key } = header || {};

  if (key === "GRADE_SCALE") {
    return _.get(boundary, "abbreviation") || _.get(boundary, "grade");
  }

  // Resolve the level label from the header key
  let levelLabel;
  if (key === "SAME_SCALE" || key === "SCORE_RANGE") {
    // same-scale or no-level-fallback: always use first level's data
    levelLabel = firstLevelLabel;
  } else if (_.startsWith(key, "SCORE_RANGE_")) {
    levelLabel = key.replace("SCORE_RANGE_", "");
  } else {
    return null;
  }

  const { id, grade } = boundary || {};
  const scores = _.get(boundary, levelLabel, {});
  const { minValue, maxValue } = scores || {};

  const minScoreKey = `${grade}_${levelLabel}_minScore`;
  const maxScoreKey = `${grade}_${levelLabel}_maxScore`;

  const { errorMsg: minScoreErrorMsg = "" } = _.get(errors, minScoreKey, {});
  const { errorMsg: maxScoreErrorMsg = "" } = _.get(errors, maxScoreKey, {});

  const minScoreHasError = errorsCount !== 0 && !_.isEmpty(minScoreErrorMsg);
  const maxScoreHasError = errorsCount !== 0 && !_.isEmpty(maxScoreErrorMsg);

  const textInputContainerClass = classNames({
    [classes.textInputContainer]: true,
    [classes.textInputLastRow]: isLastRow,
    [classes.textInputLastRowViewMode]: isLastRow && showModalInViewModal,
    [classes.textInputViewMode]: mode === "view",
  });

  return (
    <div className={textInputContainerClass}>
      {isEditable ? (
        <ConditionalWrapper
          condition={minScoreHasError}
          wrapper={
            <Tooltip
              placement={"left"}
              tooltip={minScoreErrorMsg}
              showArrow={true}
            />
          }
          type="wrapperAsElement"
        >
          <TextInput
            size={"large"}
            value={minValue}
            style={styles.textInputStyles}
            onChange={e => {
              onChangeInput({
                id,
                isMinValue: true,
                value: e.target.value,
                levelLabel,
                index,
              });
            }}
            disabled={isFirstRow || disableTextInput}
            error={minScoreHasError}
            autoFocus={autoFocusKey === minScoreKey}
            onFocus={() => setAutoFocusKey(minScoreKey)}
            onBlur={() => setAutoFocusKey("")}
          />
        </ConditionalWrapper>
      ) : (
        <div className={classes.score}>{minValue}</div>
      )}

      {isLastRow ? <div className={classes.hyphen}>{"-"}</div> : null}

      {isLastRow && isEditable && (
        <ConditionalWrapper
          condition={maxScoreHasError}
          wrapper={
            <Tooltip
              placement={"right"}
              tooltip={maxScoreErrorMsg}
              showArrow={true}
            />
          }
          type="wrapperAsElement"
        >
          <TextInput
            size={"large"}
            value={maxValue}
            style={styles.textInputStyles}
            onChange={e => {
              onChangeInput({
                id,
                isMinValue: false,
                value: e.target.value,
                levelLabel,
                index,
              });
            }}
            error={maxScoreHasError}
            disabled={disableTextInput}
            autoFocus={autoFocusKey === maxScoreKey}
            onFocus={() => setAutoFocusKey(maxScoreKey)}
            onBlur={() => setAutoFocusKey("")}
          />
        </ConditionalWrapper>
      )}
      {isLastRow && !isEditable && (
        <div className={classes.score}>{maxValue}</div>
      )}
      {!isLastRow && (
        <div className={classes.orHigherText}>
          {t("academicSetup:percentage_or_higher")}
        </div>
      )}
    </div>
  );
};

/**
 * V2: Builds table column headers dynamically from levelTags.
 * - No levels (noLevels=true): single "Score Range" column.
 * - isSameScale: single "Score Range (all levels)" column.
 * - Otherwise: one column per level tag, key = `SCORE_RANGE_${tag.label}`.
 *
 * scoreLabelFn (optional, from tableParams): called with tag.label to produce
 * the column header text (used in split-view for localised "Score label (SL)" format).
 */
const getTableHeaders = params => {
  const {
    t,
    isSameScale = false,
    levelTags = [],
    noLevels,
    isEditable,
    tableParams,
  } = params;

  const {
    style,
    gradeScaleLabel = t("academicSetup:grade_scale"),
    sameScaleLabel = t("academicSetup:score_range_for_all_levels"),
    scoreRangeLabel = t("assessmentEvaluation:score_range"),
    scoreLabelFn,
    alignment: { gradeScaleAlignment, scoreRangeAlignment } = {},
  } = tableParams || {};

  const alignment = isEditable ? "center" : "left";
  const headerItems = [
    {
      key: "GRADE_SCALE",
      value: gradeScaleLabel,
      alignment: gradeScaleAlignment || alignment,
      style,
    },
  ];

  if (isSameScale) {
    headerItems.push({
      key: "SAME_SCALE",
      value: sameScaleLabel,
      alignment: scoreRangeAlignment || alignment,
      style,
    });
  } else if (noLevels) {
    headerItems.push({
      key: "SCORE_RANGE",
      value: scoreRangeLabel,
      alignment: scoreRangeAlignment || alignment,
      style,
    });
  } else {
    _.forEach(levelTags, tag => {
      const columnLabel = scoreLabelFn ? scoreLabelFn(tag.label) : tag.label;
      headerItems.push({
        key: `SCORE_RANGE_${tag.label}`,
        value: columnLabel,
        alignment: scoreRangeAlignment || alignment,
        style,
      });
    });
  }

  return headerItems;
};

const getTableData = params => {
  const { headers, gradeBoundary = [] } = params || {};

  const data = _.map(gradeBoundary, (boundary, index) => {
    const isLastRow = index === _.size(gradeBoundary) - 1;
    const isFirstRow = index === 0;

    const rowData = _.map(headers, header => {
      return {
        key: _.get(header, "key"),
        value: getTableRowValue({
          header,
          boundary,
          isFirstRow,
          isLastRow,
          index,
          ...params,
        }),
      };
    });

    return {
      id: _.get(boundary, "grade"),
      rowData,
    };
  });

  return data;
};

/**
 * V2: Validates scores for any set of level tags.
 * When isSameScale=true, only the first level is validated (others mirror it).
 * When noLevels=true, falls back to a single "SL" key validation.
 *
 * Error key format: `${grade}_${levelLabel}_minScore`
 */
/**
 * Validates a single level column for two consecutive boundary rows.
 */
const validateLevelBetweenRows = ({
  t,
  id,
  nextId,
  levelLabel,
  current,
  nextBoundary,
  isFirstRow,
  isLastRow,
  totalRows,
}) => {
  const errors = {};
  const { minValue: _currentMin, maxValue: _currentMax } = current;
  const currentMin = parseFloat(_currentMin);
  const currentMax = parseFloat(_currentMax);

  if (nextBoundary) {
    const next = _.get(nextBoundary, levelLabel, {});
    const nextMin = parseFloat(_.get(next, "minValue"));
    if (currentMin >= nextMin) {
      const errorKey = `${isFirstRow ? nextId : id}_${levelLabel}_minScore`;
      const msgKey = isFirstRow
        ? "min_score_must_be_higher_than_label"
        : "min_score_lower_than_max_score_label";
      errors[errorKey] = {
        errorMsg: t(`academicSetup:${msgKey}`, {
          label: isFirstRow ? currentMin : nextMin,
        }),
      };
    }
  }

  if (_.isEmpty(_.toString(_currentMin))) {
    errors[`${id}_${levelLabel}_minScore`] = {
      errorMsg: t("academicSetup:this_field_is_compulsory"),
    };
  }

  if (isLastRow && totalRows > 1 && currentMin > currentMax) {
    errors[`${id}_${levelLabel}_minScore`] = {
      errorMsg: t("academicSetup:min_score_higher_than_max_score_label", {
        label: currentMax,
      }),
    };
  }

  return errors;
};

const checkAreScoresValid = ({
  t,
  categoryGradeBoundaryValues = [],
  levelTags = [],
  noLevels,
  isSameScale,
}) => {
  let effectiveLevels;
  if (isSameScale) {
    effectiveLevels = _.slice(levelTags, 0, 1);
  } else if (noLevels) {
    effectiveLevels = [{ label: "SL" }];
  } else {
    effectiveLevels = levelTags;
  }

  let errors = {};
  const totalRows = _.size(categoryGradeBoundaryValues);

  for (let i = 0; i < totalRows; i++) {
    const boundary = categoryGradeBoundaryValues[i];
    const nextBoundary = categoryGradeBoundaryValues[i + 1];
    const id = _.get(boundary, "grade");
    const nextId = _.get(nextBoundary, "grade");
    const isLastRow = i === totalRows - 1;
    const isFirstRow = i === 0;

    _.forEach(effectiveLevels, ({ label: levelLabel }) => {
      const current = _.get(boundary, levelLabel, {});
      const levelErrors = validateLevelBetweenRows({
        t,
        id,
        nextId,
        levelLabel,
        current,
        nextBoundary,
        isFirstRow,
        isLastRow,
        totalRows,
      });
      errors = { ...errors, ...levelErrors };
    });
  }

  return errors;
};

/**
 * V2: Checks if all levels have identical score ranges for every grade row.
 * Used to auto-detect isSameScale when loading an existing custom boundary.
 */
const checkIsSameScale = ({ categoryGradeBoundaryValues, levelTags }) => {
  if (
    _.isEmpty(levelTags) ||
    _.size(levelTags) < 2 ||
    _.isEmpty(categoryGradeBoundaryValues)
  ) {
    return false;
  }

  const firstLabel = _.get(_.first(levelTags), "label");
  let isSame = true;

  _.forEach(categoryGradeBoundaryValues, item => {
    const firstValues = _.get(item, firstLabel, {});
    _.forEach(_.tail(levelTags), ({ label }) => {
      const otherValues = _.get(item, label, {});
      if (
        !_.isEqual(
          _.get(firstValues, "minValue"),
          _.get(otherValues, "minValue")
        ) ||
        !_.isEqual(
          _.get(firstValues, "maxValue"),
          _.get(otherValues, "maxValue")
        )
      ) {
        isSame = false;
        return false; // break _.forEach
      }
    });
    if (!isSame) return false;
  });

  return isSame;
};

/**
 * V2: Copies the first level's score values to all other levels.
 * Used when teacher checks "Use same scale for all levels".
 */
const copyFirstLevelToAllLevels = ({
  categoryGradeBoundaryValues,
  levelTags,
}) => {
  if (_.isEmpty(levelTags) || _.size(levelTags) < 2) {
    return categoryGradeBoundaryValues;
  }

  const firstLabel = _.get(_.first(levelTags), "label");
  const updated = _.cloneDeep(categoryGradeBoundaryValues);

  _.forEach(updated, (item, index) => {
    const firstLevelValues = _.get(item, firstLabel, {});
    _.forEach(_.tail(levelTags), ({ label }) => {
      _.set(updated, [index, label], _.cloneDeep(firstLevelValues));
    });
  });

  return updated;
};

/**
 * V2: Returns true if any required score cell is empty, disabling the Save button.
 * Checks all effective levels (or only the first when isSameScale=true).
 */
const getIsSaveButtonDisabled = ({
  gradeBoundary,
  levelTags,
  noLevels,
  isSameScale,
}) => {
  let effectiveLevels;
  if (isSameScale) {
    effectiveLevels = _.slice(levelTags, 0, 1);
  } else if (noLevels) {
    effectiveLevels = [{ label: "SL" }];
  } else {
    effectiveLevels = levelTags;
  }

  return _.reduce(
    gradeBoundary,
    (acc, value, index) => {
      if (acc) return acc;
      const isLastRow = index === _.size(gradeBoundary) - 1;

      return _.some(effectiveLevels, ({ label }) => {
        const scores = _.get(value, label, {});
        const { minValue, maxValue } = scores;
        return !_.toString(minValue) || (isLastRow && !_.toString(maxValue));
      });
    },
    false
  );
};

/**
 * V2: Merges abbreviations from ordered source categories into the
 * gradeBoundaryValues so the GRADE_SCALE column can display them.
 */
const applyAbbreviationsToGradeBoundary = ({
  gradeBoundaryValues,
  sourceCategories,
  subjectType,
}) => {
  if (_.isEmpty(sourceCategories) || _.isEmpty(gradeBoundaryValues)) {
    return gradeBoundaryValues;
  }
  const isTOKSubject = _.isEqual(subjectType, "TOK");
  const orderedCategories = _.orderBy(
    sourceCategories,
    "label",
    isTOKSubject ? "desc" : "asc"
  );
  return _.map(gradeBoundaryValues, (item, index) => {
    const abbreviation = _.get(orderedCategories, [index, "abbreviation"]);
    return abbreviation ? { ...item, abbreviation } : item;
  });
};

const TableWithLoader = withLoader(Table);

/**
 * V2: Replaces getHasHLOrSLWeight.
 * Simply returns the assignment's tagged levels directly – no hardcoded SL/HL check.
 */
const getLevelWeights = ({ subjectLevelGenericTags = [] }) => {
  return {
    levelTags: subjectLevelGenericTags,
    noLevels: _.isEmpty(subjectLevelGenericTags),
  };
};

/**
 * V2: Converts a gradeScale object (from gradingCategoryValues.gradeScale)
 * into the old categoryGradeBoundaries shape that getCategoryGradeBoundaryValues
 * already understands:
 *   { categories: [{ id, label, values: [{ id, minValue, maxValue, genericTag: { id, label } }] }] }
 *
 * Falls back to the default entity detail, or the first one if none is marked default.
 * When a gradeScaleValueScore has no subjectLevel (no-levels assignment),
 * genericTag.label defaults to "SL".
 */
const transformGradeScaleToGradeBoundaries = ({ gradeScale }) => {
  const entityDetails = _.get(
    _.first(gradeScale),
    "gradeScaleEntityDetails",
    []
  );
  const entityDetail =
    _.find(entityDetails, "isDefault") || _.first(entityDetails);
  const gradeScaleValues = _.get(entityDetail, "gradeScaleValues", []);

  const categories = _.map(gradeScaleValues, gsv => ({
    id: gsv.id,
    label: gsv.label,
    abbreviation: gsv.abbreviation,
    values: _.map(_.get(gsv, "gradeScaleValueScores", []), score => ({
      id: score.id,
      minValue: score.minValue,
      maxValue: score.maxValue,
      genericTag: {
        id: _.get(score, "subjectLevel.id", null),
        label: _.get(score, "subjectLevel.label", "SL"),
      },
    })),
  }));

  return { categories };
};

/**
 * V2: Finds the gradeScaleEntityDetail whose academicTerm.id matches
 * the given academicTermId, scanning all gradeScales returned by the query.
 * Returns { termEntityDetail, hasTermGradeBoundary }.
 */
const getTermGradeScaleEntityDetail = ({ gradeScales, academicTermId }) => {
  let termEntityDetail;
  let isIBDefined = false;
  _.forEach(gradeScales, gradeScale => {
    const found = _.find(
      _.get(gradeScale, "gradeScaleEntityDetails", []),
      detail => _.get(detail, "academicTerm.id") === academicTermId
    );
    if (found) {
      termEntityDetail = found;
      isIBDefined = _.get(gradeScale, "type") === "DP_IB_DEFINED";
      return false; // break lodash forEach
    }
  });
  return {
    termEntityDetail,
    hasTermGradeBoundary: !!termEntityDetail && isIBDefined,
  };
};

/**
 * V2: Converts a gradeScaleEntityDetail (from getSubjectTermGradeScaleQuery)
 * into the old termGradeBoundaries shape that getFilteredGradeBoundaries and
 * getCategoryGradeBoundaryValues already understand:
 *   { categories: [{ id, label, values: [{ id, minValue, maxValue, genericTag: { id, label } }] }] }
 *
 * When a gradeScaleValueScore has no subjectLevel (no-levels assignment),
 * genericTag.label defaults to "SL".
 */
const transformTermEntityDetailToGradeBoundaries = ({ termEntityDetail }) => {
  if (!termEntityDetail) return {};
  const gradeScaleValues = _.get(termEntityDetail, "gradeScaleValues", []);
  const categories = _.map(gradeScaleValues, gsv => ({
    id: gsv.id,
    label: gsv.label,
    abbreviation: gsv.abbreviation,
    values: _.map(_.get(gsv, "gradeScaleValueScores", []), score => ({
      id: score.id,
      minValue: score.minValue,
      maxValue: score.maxValue,
      genericTag: {
        id: _.get(score, "subjectLevel.id", null),
        label: _.get(score, "subjectLevel.label", "SL"),
      },
    })),
  }));
  return { categories };
};

/**
 * V2: Given gradingConfigurations from the new query, finds the gradeScale
 * for the selected category, respecting differentCategoriesForGradingPeriods.
 *
 * Logic:
 * 1. Get differentCategoriesForGradingPeriods from the first config's settings.
 * 2. Pick the right gradingCategorySet:
 *    - If differentCategoriesForGradingPeriods AND academicTermId present
 *      → the set whose academicTerm.id matches academicTermId
 *    - Otherwise → the set with no academicTerm (global set)
 *    - Fallback → first set
 * 3. Within the set, find the gradingCategoryValue whose globalCategory.id === categoryId.
 * 4. Return { gradeScale, hasCategoryGradeBoundary }.
 */
const getCategoryGradeScaleFromConfig = ({
  gradingConfigurations,
  categoryId,
  academicTermId,
}) => {
  const emptyResult = { gradeScale: null, hasCategoryGradeBoundary: false };

  if (_.isEmpty(gradingConfigurations) || !categoryId) return emptyResult;

  const gradingConfig = _.first(gradingConfigurations);

  const {
    differentCategoriesForGradingPeriods,
  } = getGradingSetupConfigurations({
    scoreGradingConfig: gradingConfig,
    isUsingNewGradingSetupInCourses: true,
  });

  const gradingCategorySets = _.get(gradingConfig, "gradingCategorySets", []);

  let selectedSet;
  if (differentCategoriesForGradingPeriods && academicTermId) {
    selectedSet = _.find(
      gradingCategorySets,
      set => _.get(set, "academicTerm.id") === academicTermId
    );
  }

  if (!selectedSet) {
    // global set: the one with no academicTerm, or fall back to first
    selectedSet =
      _.find(gradingCategorySets, set =>
        _.isEmpty(_.get(set, "academicTerm.id"))
      ) || _.first(gradingCategorySets);
  }

  const gradingCategoryValues = _.get(selectedSet, "gradingCategoryValues", []);
  const matchedValue = _.find(
    gradingCategoryValues,
    val => _.get(val, "globalCategory.id") === categoryId
  );

  const gradeScale = _.get(matchedValue, "gradeScale", null);
  const isIBDefined = _.get(_.first(gradeScale), "type") === "DP_IB_DEFINED";
  return {
    gradeScale,
    hasCategoryGradeBoundary: !_.isEmpty(gradeScale) && isIBDefined,
  };
};

/**
 * Renders the view/preview mode of the IB Scale Tool.
 * Extracted to reduce cognitive complexity of the main component.
 */
const renderIBScaleViewMode = ({
  t,
  classes,
  toolStyles,
  location,
  assessmentTool,
  ibPYPElementRatings,
  academicCriteriaValues,
  isGeneratingAIResponse,
  customRatingElementClass,
  customSelectedRatingElement,
  isData,
  isLoading,
  getTableProps,
  onlyShowTable,
  isSplitView,
  showAccordian,
  showTitle,
  viewTableContainerStyle,
  showPopover,
  setShowPopover,
  infoIconRef,
  maxScore,
  updateQueryParameters,
  title,
  toggleAssessmentToolModal,
  showModalInViewModal,
  assessmentToolType,
  isEditable,
  mode,
}) => {
  const { gradeScaleLabel = {} } = toolStyles || {};
  const isExpanded = _.get(location, "query.isExpanded", false);

  const assessmentToolId = _.get(assessmentTool, "id");

  const selectedCriteria = _.find(ibPYPElementRatings, rating => {
    return (
      _.get(rating, "ibPYPElementId") === assessmentToolId && !rating?.isDeleted
    );
  });

  // The academicCriteriaValue.id stored in the rating may come from a different
  // entity (AcademicCriteriaValue) than the gradeScaleValues used to build
  // options here. Match by label to find the correct option ID.
  const selectedGradeLabel =
    _.get(selectedCriteria, "academicCriteriaValue.label") ||
    _.get(selectedCriteria, "value");
  const matchingOption = _.find(
    academicCriteriaValues,
    opt => opt.label === selectedGradeLabel
  );
  const criteriaRatingValue =
    _.get(matchingOption, "id") ||
    _.get(selectedCriteria, "academicCriteriaValue.id", "");

  const criteriaRating = (
    <Tooltip
      placement={"left"}
      tooltip={t("common:ib_grade_automatically_calculated")}
      showArrow={true}
    >
      <div>
        {isGeneratingAIResponse ? (
          <div className={classes.skeletonContainer}>
            {_.map(academicCriteriaValues, item => {
              return <SkeletonLoader key={item.id} width={44} height={32} />;
            })}
          </div>
        ) : (
          <CriteriaRating
            isEvaluation={false}
            options={_.orderBy(academicCriteriaValues, "label")}
            value={criteriaRatingValue}
            isViewMode={true}
            shouldUpdateOnValueChnage={true}
            containerStyle={styles.criteriaRatingContainerStyle}
            customViewModeClass={classes.customViewModeClass}
            customRatingElementClass={
              customRatingElementClass
                ? customRatingElementClass
                : classes.customRatingElementClass
            }
            customSelectedRatingElement={
              customSelectedRatingElement
                ? customSelectedRatingElement
                : classes.customSelectedRatingElement
            }
          />
        )}
      </div>
    </Tooltip>
  );

  const popoverContainer = (
    <div style={viewTableContainerStyle} className={classes.viewTableContainer}>
      <TableWithLoader
        isData={isData}
        isLoading={isLoading}
        showBorder={true}
        {...getTableProps()}
      />
    </div>
  );

  if (onlyShowTable) {
    return popoverContainer;
  }

  if (isSplitView) {
    return (
      <div className={classes.splitViewContainer}>
        <div className={classes.ibGradeLabel}>
          {showTitle && (
            <span style={gradeScaleLabel}>{t("common:ib_grade")}</span>
          )}
          <div className={classes.infoIconContainer} ref={infoIconRef}>
            <Popover
              isOpen={showPopover}
              positions={["bottom", "top"]}
              align={"end"}
              onClickOutside={() => setShowPopover(false)}
              containerStyle={styles.popoverContainerStyle}
              content={({ position, childRect, popoverRect }) => (
                <ArrowContainer
                  position={position}
                  childRect={childRect}
                  popoverRect={popoverRect}
                  arrowColor={colors.white}
                  arrowSize={8}
                  arrowStyle={styles.arrowStyle}
                >
                  {popoverContainer}
                </ArrowContainer>
              )}
            >
              <div
                onClick={() => setShowPopover(!showPopover)}
                onKeyDown={e => {
                  if (e.key === "Enter") setShowPopover(!showPopover);
                }}
                role="button"
                tabIndex={0}
                aria-label={t("common:ib_grade_scale")}
              >
                <InformationOutlined size={"x-small"} variant={"subtle"} />
              </div>
            </Popover>
          </div>
        </div>
        {criteriaRating}
      </div>
    );
  }

  const customTitleComponent = showTitle ? (
    <header className={classes.header}>
      <div className={classes.headerLeftContainer}>
        <div className={classes.ibGradeScale} style={gradeScaleLabel}>
          {t("common:ib_grade_scale")}
        </div>
        <div className={classes.automatedGrading}>
          {t("classRoom:automated_grading")}
          {" - "}
          {getAutoEvaluateIBGradeToolTypeName({ t, assessmentToolType })}
        </div>
      </div>
      <div className={classes.headerRightContainer}>
        {showModalInViewModal ? (
          <IconButton
            dsVersion="2.0"
            icon={<CloseOutlined variant="subtle" />}
            size={"medium"}
            variant="neutral"
            type="plain"
            onClick={toggleAssessmentToolModal}
          />
        ) : (
          criteriaRating
        )}
      </div>
    </header>
  ) : null;

  const expandedContent = (
    <div className={classes.viewTableContainer} style={viewTableContainerStyle}>
      <TableWithLoader
        isData={isData}
        isLoading={isLoading}
        showBorder={true}
        isHeaderFixed={isEditable}
        {...getTableProps()}
      />
    </div>
  );

  const modalContentClass = classNames(
    classes.modalContent,
    classes.viewModeModalContent
  );

  if (!showAccordian) {
    return (
      <React.Fragment>
        {customTitleComponent}
        {expandedContent}
      </React.Fragment>
    );
  }

  return (
    <ConditionalWrapper
      wrapper={<UIModal isOpen modalContent={modalContentClass} />}
      condition={showModalInViewModal}
      type={"wrapperAsElement"}
    >
      {showModalInViewModal ? (
        <React.Fragment>
          <div className={classes.customTitleComp}>{customTitleComponent}</div>
          <TableWithLoader
            isData={isData}
            isLoading={isLoading}
            showBorder={true}
            isHeaderFixed={isEditable}
            {...getTableProps()}
          />
        </React.Fragment>
      ) : (
        <Accordian
          customTitleComponent={customTitleComponent}
          expandedContent={expandedContent}
          hideContainerBorder={true}
          showLeftArrow={true}
          customLeftIcon={
            <IconButton
              dsVersion="2.0"
              icon={<ChevronRightOutlined variant="subtle" />}
              variant="neutral"
              type="plain"
              size={"x-small"}
            />
          }
          isLeftIconContainerFocusable={false}
          leftArrowStyle={styles.leftArrowStyle}
          headerStyle={styles.headerStyle}
          showLabelBorder={false}
          getIsExpanded={value => {
            updateQueryParameters({
              params: {
                isExpanded: !value,
              },
              navigateType: "replace",
            });
          }}
          isExpanded={JSON.parse(isExpanded)}
        />
      )}
    </ConditionalWrapper>
  );
};

const AutomaticIBScaleToolV2 = props => {
  const {
    t,
    subjectLevelGenericTags,
    subjectType,
    toggleAssessmentToolModal,
    mode = "create",
    categoryGradeBoundaries,
    termGradeBoundaries,
    categoryGradeBoundaryValues,
    isData,
    isLoading,
    assessmentTool = {},
    academicCriteriaValues,
    ibPYPElementRatings,
    isEditable,
    isToolCreated,
    hasTermGradeBoundary,
    hasCategoryGradeBoundary,
    updateCategoryGradeBoundary,
    setToastMsg,
    title,
    categoryId,
    showModalInViewModal = true,
    updateQueryParameters,
    location,
    assessmentToolType,
    onlyShowTable = false,
    tableParams = {},
    maxScore,
    isSplitView = false,
    customRatingElementClass,
    customSelectedRatingElement,
    showAccordian = true,
    toolStyles,
    showTitle = true,
    viewTableContainerStyle = {},
    portalType,
    isGeneratingAIResponse,
  } = props;

  // V2: Derive dynamic level tags from the assignment's tagged subject levels
  const { levelTags, noLevels } = getLevelWeights({ subjectLevelGenericTags });
  const firstLevelLabel = _.get(_.first(levelTags), "label", "SL");

  // states
  const [isSameScale, setIsSameScale] = useState(false);
  const [showFullScreenLoader, setShowFullScreenLoader] = useState(false);
  const [gradeBoundary, setGradeBoundary] = useState(
    categoryGradeBoundaryValues
  );
  const [autoFocusKey, setAutoFocusKey] = useState("");
  const [gradeBoundaryMethod, setGradeBoundaryMethod] = useState("");
  const [errorsCount, setErrorsCount] = useState(0);
  const [isSaveBtnDisabled, setIsSaveBtnDisabled] = useState(false);
  const [showPopover, setShowPopover] = useState(false);

  const infoIconRef = useRef(null);

  // side effects

  const gradeBoundaryDependencyArray =
    mode === "preview"
      ? [subjectType, academicCriteriaValues, categoryGradeBoundaryValues]
      : [subjectType, academicCriteriaValues];

  useEffect(() => {
    if (!_.isEmpty(academicCriteriaValues)) {
      const updatedCategoryGradeBoundaries = getCategoryGradeBoundaryValues({
        categoryGradeBoundaries: getFilteredGradeBoundaries({
          gradeBoundary: {
            categories: academicCriteriaValues,
          },
          subjectLevelGenericTags,
          scoreKey: "scoreValues",
        }),
        categoryGradeBoundaryValues,
        subjectType,
      }).slice(0, _.size(academicCriteriaValues));

      setGradeBoundary(
        applyAbbreviationsToGradeBoundary({
          gradeBoundaryValues: updatedCategoryGradeBoundaries,
          sourceCategories: academicCriteriaValues,
          subjectType,
        })
      );
    }
  }, gradeBoundaryDependencyArray); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!_.includes(["view", "preview"], mode)) {
      if (isToolCreated) {
        let key = "";
        switch (assessmentToolType) {
          case ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY:
            key = "category_boundary";
            break;
          case ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY:
            key = "term_boundary";
            break;
          default:
            key = "custom_boundary";
        }

        setGradeBoundaryMethod(key);
      } else {
        if (hasCategoryGradeBoundary) {
          const newGradeBoundaryValues = getCategoryGradeBoundaryValues({
            categoryGradeBoundaries,
            categoryGradeBoundaryValues,
            subjectType,
          }).slice(0, _.size(categoryGradeBoundaries?.categories));

          setGradeBoundaryMethod("category_boundary");
          setGradeBoundary(
            applyAbbreviationsToGradeBoundary({
              gradeBoundaryValues: newGradeBoundaryValues,
              sourceCategories: _.get(categoryGradeBoundaries, "categories"),
              subjectType,
            })
          );
        } else if (hasTermGradeBoundary) {
          const newGradeBoundaryValues = getCategoryGradeBoundaryValues({
            categoryGradeBoundaries: termGradeBoundaries,
            categoryGradeBoundaryValues,
            subjectType,
          }).slice(0, _.size(termGradeBoundaries?.categories));

          setGradeBoundaryMethod("term_boundary");
          setGradeBoundary(
            applyAbbreviationsToGradeBoundary({
              gradeBoundaryValues: newGradeBoundaryValues,
              sourceCategories: _.get(termGradeBoundaries, "categories"),
              subjectType,
            })
          );
        } else {
          setGradeBoundaryMethod("custom_boundary");
          setGradeBoundary(categoryGradeBoundaryValues);
        }
      }
    }
  }, [hasCategoryGradeBoundary, hasTermGradeBoundary, subjectType]); // eslint-disable-line react-hooks/exhaustive-deps

  // V2: uses checkIsSameScale which works for any number of levels
  useEffect(() => {
    if (
      assessmentToolType === ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY &&
      gradeBoundaryMethod === "custom_boundary" &&
      isToolCreated &&
      mode !== "view"
    ) {
      setIsSameScale(
        checkIsSameScale({
          categoryGradeBoundaryValues: gradeBoundary,
          levelTags,
        })
      );
    }
  }, [isToolCreated, gradeBoundary, levelTags]); // eslint-disable-line react-hooks/exhaustive-deps

  // V2: uses checkAreScoresValid and getIsSaveButtonDisabled
  // Only validate for custom boundaries — category/term boundaries are read-only
  // and come from the query in descending order which would always fail cross-row validation.
  const errors =
    gradeBoundaryMethod === "custom_boundary"
      ? checkAreScoresValid({
          t,
          categoryGradeBoundaryValues: gradeBoundary,
          levelTags,
          noLevels,
          isSameScale,
        })
      : {};

  useEffect(() => {
    if (mode !== "view") {
      const newIsSaveButtonDisabled = getIsSaveButtonDisabled({
        gradeBoundary,
        levelTags,
        noLevels,
        isSameScale,
      });
      setIsSaveBtnDisabled(newIsSaveButtonDisabled);

      if (!newIsSaveButtonDisabled) {
        setErrorsCount(_.size(errors));
      }
    }

    return () => setErrorsCount(0);
  }, [
    gradeBoundary,
    levelTags,
    noLevels,
    isSameScale,
    mode,
    gradeBoundaryMethod,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // functions

  // V2: copies first level values to all levels (not just SL→HL)
  const onChangeScale = useCallback(() => {
    if (!isSameScale) {
      const gradeBoundaryValues = copyFirstLevelToAllLevels({
        categoryGradeBoundaryValues: gradeBoundary,
        levelTags,
      });

      setGradeBoundary(gradeBoundaryValues);
    }
    setIsSameScale(!isSameScale);
  }, [isSameScale, gradeBoundary, levelTags]);

  // V2: takes levelLabel string instead of isHL boolean
  const onChangeInput = useCallback(
    params => {
      const { levelLabel, isMinValue, value, index } = params;

      // allow only numbers and up to 2 decimals
      if (!DOUBLE_DIGIT_DECIMAL_NUMBERS_REGEX.test(value) && value) return;

      const valueToUpdate = isMinValue ? "minValue" : "maxValue";
      const updatedValues = _.cloneDeep(gradeBoundary);

      if (isSameScale) {
        // propagate typed value to all level keys
        _.forEach(levelTags, ({ label }) => {
          _.set(updatedValues, [index, label, valueToUpdate], value);
          if (index > 0 && isMinValue) {
            _.set(updatedValues, [index - 1, label, "maxValue"], value);
          }
        });
      } else {
        _.set(updatedValues, [index, levelLabel, valueToUpdate], value);
        if (index > 0 && isMinValue) {
          _.set(updatedValues, [index - 1, levelLabel, "maxValue"], value);
        }
      }

      setGradeBoundary(updatedValues);
      setIsSaveBtnDisabled(
        getIsSaveButtonDisabled({
          gradeBoundary: updatedValues,
          levelTags,
          noLevels,
          isSameScale,
        })
      );
    },
    [gradeBoundary, isSameScale, levelTags, noLevels]
  );

  const getTableProps = () => {
    // V2: split-view uses a scoreLabelFn for dynamic level column labels
    const splitViewTableParams = isSplitView
      ? {
          style: {
            height: "42px",
            minWidth: "88px",
            maxWidth: "132px",
          },
          gradeScaleLabel: t("common:ib_grade"),
          scoreLabelFn: label => t("common:score_label", { label }),
          alignment: {
            gradeScaleAlignment: "center",
            scoreRangeAlignment: "left",
          },
        }
      : tableParams;

    // V2: pass levelTags + noLevels; removed hasSLWeight/hasHLWeight
    const headers = getTableHeaders({
      t,
      isSameScale,
      levelTags,
      noLevels,
      isEditable,
      tableParams: splitViewTableParams,
    });

    const data = getTableData({
      t,
      headers,
      subjectType,
      gradeBoundary,
      onChangeInput,
      isEditable,
      mode,
      errors,
      autoFocusKey,
      setAutoFocusKey,
      disableTextInput: gradeBoundaryMethod !== "custom_boundary",
      errorsCount,
      showModalInViewModal,
      firstLevelLabel,
    });

    return { headers, data };
  };

  const onClickSave = async () => {
    if (_.size(errors) > 0) {
      setToastMsg({
        msg: "toastMsgs:rectify_errors_with_count",
        locale_params: [
          { key: "count", value: _.size(errors), isPlainText: true },
        ],
        type: "warning",
        position: "toast-top-center",
      });
      return;
    }

    const {
      createAssessmentTool,
      parentId,
      parentType,
      assessmentId,
      categoryPointSetId,
      termPointSetId,
      removeAssessmentTool,
    } = props;

    setShowFullScreenLoader(true);

    const shouldCreateNewTool = getShouldCreateNewTool({
      gradeBoundaryMethod,
      assessmentToolType,
    });

    if (_.isEmpty(assessmentTool) || shouldCreateNewTool) {
      if (shouldCreateNewTool) {
        // delete previous tool
        await removeAssessmentTool({
          id: _.get(assessmentTool, "id"),
          type: assessmentToolType,
          parentId,
          parentType,
          portalType,
        });
      }

      let criteriaSetId = "";
      let type = ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY;

      switch (gradeBoundaryMethod) {
        case "category_boundary":
          criteriaSetId = categoryPointSetId;
          type = ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY;
          break;
        case "term_boundary":
          criteriaSetId = termPointSetId;
          type = ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY;
          break;
      }

      const extraParams = {
        label: type,
      };

      if (criteriaSetId) {
        extraParams["criteriaSetId"] = criteriaSetId;
      } else {
        extraParams["associatedEntites"] = [
          {
            id: parentId,
            type: parentType,
          },
        ];

        // V2: pass levelTags as subjectLevelGenericTags; getCategoriesForGradeBoundaryTool
        // already handles any level labels dynamically
        const categories = getCategoriesForGradeBoundaryTool({
          subjectType,
          gradeBoundary,
          subjectLevelGenericTags: levelTags,
          type,
        });

        if (_.isEmpty(categories)) {
          setToastMsg("toastMsgs:something_went_wrong");
          setShowFullScreenLoader(false);
          return;
        }

        extraParams["categories"] = categories;
      }

      await createAssessmentTool({
        type,
        parentId,
        parentType,
        assessmentId,
        extraParams,
        portalType,
      });
    } else {
      if (gradeBoundaryMethod === "custom_boundary") {
        const extraParams = {
          subType: "ASSESSMENT_CUSTOM_GRADE_BOUNDARY",
        };

        // V2: pass levelTags as subjectLevelGenericTags
        await updateCategoryGradeBoundary({
          categoryGradeBoundaryValues: gradeBoundary,
          subjectLevelGenericTags: levelTags,
          gradeBoundaryId: _.get(assessmentTool, "id"),
          extraParams,
          portalType,
        });
      }
    }
    setShowFullScreenLoader(false);

    toggleAssessmentToolModal?.();
  };

  // ─── View / Preview mode ────────────────────────────────────────────────────

  if (mode === "view" || mode === "preview") {
    return renderIBScaleViewMode({
      t,
      classes,
      toolStyles,
      location,
      assessmentTool,
      ibPYPElementRatings,
      academicCriteriaValues,
      isGeneratingAIResponse,
      customRatingElementClass,
      customSelectedRatingElement,
      isData,
      isLoading,
      getTableProps,
      onlyShowTable,
      isSplitView,
      showAccordian,
      showTitle,
      viewTableContainerStyle,
      showPopover,
      setShowPopover,
      infoIconRef,
      maxScore,
      updateQueryParameters,
      title,
      toggleAssessmentToolModal,
      showModalInViewModal,
      assessmentToolType,
      isEditable,
      mode,
    });
  }

  // ─── Edit / Create mode ─────────────────────────────────────────────────────

  // V2: show same-scale checkbox when there are 2+ levels and custom method selected
  const hasMultipleLevels = _.size(levelTags) > 1;

  const onClickGradeBoundary = e => {
    const key = _.get(e, "key");

    if (key === gradeBoundaryMethod) return;

    switch (key) {
      case "category_boundary":
        {
          const newGradeBoundaryValues = getCategoryGradeBoundaryValues({
            categoryGradeBoundaries,
            categoryGradeBoundaryValues,
            subjectType,
          }).slice(0, _.size(categoryGradeBoundaries?.categories));
          setGradeBoundary(
            applyAbbreviationsToGradeBoundary({
              gradeBoundaryValues: newGradeBoundaryValues,
              sourceCategories: _.get(categoryGradeBoundaries, "categories"),
              subjectType,
            })
          );
          setIsSameScale(false);
        }
        break;
      case "term_boundary":
        {
          const newGradeBoundaryValues = getCategoryGradeBoundaryValues({
            categoryGradeBoundaries: termGradeBoundaries,
            categoryGradeBoundaryValues,
            subjectType,
          }).slice(0, _.size(termGradeBoundaries?.categories));
          setGradeBoundary(
            applyAbbreviationsToGradeBoundary({
              gradeBoundaryValues: newGradeBoundaryValues,
              sourceCategories: _.get(termGradeBoundaries, "categories"),
              subjectType,
            })
          );
          setIsSameScale(false);
        }
        break;
      default: {
        if (
          !isToolCreated ||
          assessmentToolType !== ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY
        ) {
          setGradeBoundary(categoryGradeBoundaryValues);
        } else {
          const newGradeBoundaryValues = getCategoryGradeBoundaryValues({
            categoryGradeBoundaries: {
              categories: _.get(assessmentTool, "academicCriteriaValues", []),
            },
            categoryGradeBoundaryValues,
            subjectType,
          });
          setGradeBoundary(newGradeBoundaryValues);
        }
      }
    }

    setGradeBoundaryMethod(key);
  };

  const gradeBoundaryOptions = getGradeBoundaryOptions({
    t,
    hasCategoryGradeBoundary,
    hasTermGradeBoundary,
    categoryId,
    assessmentToolType,
  });

  const buttonLabel = _.find(
    gradeBoundaryOptions,
    ({ key }) => key === gradeBoundaryMethod
  )?.label;

  return (
    <UIModal
      isOpen={true}
      modalContent={classes.modalContent}
      shouldCloseOnOverlayClick={false}
    >
      {isLoading ? (
        <Loading />
      ) : (
        <div className={classes.container}>
          <header className={classes.header}>
            <div className={classes.headerLeftContainer}>
              <ConditionalWrapper
                type={"wrapperAsElement"}
                condition={_.size(title) > 50}
                wrapper={
                  <Tooltip placement={"top"} tooltip={title} showArrow={true} />
                }
              >
                <div className={classes.headerTitle}>
                  {t("classRoom:define_grade_boundary_for_label", {
                    label: title,
                  })}
                </div>
              </ConditionalWrapper>
            </div>
            <div className={classes.headerRightContainer}>
              <IconButton
                dsVersion="2.0"
                icon={<CloseOutlined variant="subtle" />}
                size={"medium"}
                variant="neutral"
                type="plain"
                onClick={toggleAssessmentToolModal}
              />
            </div>
          </header>

          <div className={classes.contentSpaceBetween}>
            <div className={classes.subtext}>
              {t("classRoom:auto_evaluate_grade_boudary_tool_subtext")}
            </div>
            <Dropdown
              overlay={
                <DropdownMenu
                  options={gradeBoundaryOptions}
                  onClick={onClickGradeBoundary}
                  hasMultilineOptions={true}
                  itemTitleContainerStyle={styles.itemTitleContainerStyle}
                />
              }
            >
              <ButtonDisclosure
                dsVersion="2.0"
                size="medium"
                variant="neutral"
                type="outlined"
              >
                {buttonLabel}
              </ButtonDisclosure>
            </Dropdown>
          </div>

          {/* V2: show "same scale" checkbox for any 2+ levels in custom mode */}
          {hasMultipleLevels && gradeBoundaryMethod === "custom_boundary" ? (
            <div
              className={classes.flexWithGap}
              onClick={onChangeScale}
              onKeyDown={e => {
                if (e.key === "Enter") onChangeScale();
              }}
              role="button"
              tabIndex={0}
            >
              <Checkbox isChecked={isSameScale} />
              <div className={classes.sameScaleLabel}>
                {t("academicSetup:use_same_scale_for_all_levels")}
              </div>
            </div>
          ) : null}

          <div className={classes.tableContainer}>
            <Table
              showBorder={true}
              isHeaderFixed={isEditable}
              {...getTableProps()}
            />
          </div>

          <footer className={classes.footer}>
            <Button
              dsVersion="2.0"
              size="medium"
              variant="neutral"
              type="outlined"
              onClick={toggleAssessmentToolModal}
            >
              {isToolCreated ? t("common:discard_changes") : t("common:cancel")}
            </Button>
            <Button
              dsVersion="2.0"
              size="medium"
              variant={"progressive"}
              onClick={onClickSave}
              disabled={isSaveBtnDisabled}
            >
              {isToolCreated ? t("common:save_changes") : t("common:save")}
            </Button>
          </footer>
        </div>
      )}

      {showFullScreenLoader ? <FullScreenLoader /> : null}
    </UIModal>
  );
};

// ─── withDerivedProps ─────────────────────────────────────────────────────────

/**
 * V2: Fallback when no course context is present (queries skipped).
 * subjectLevelGenericTags is NOT derived here – it comes from mapStateToProps
 * via assessmentFields.subjectLevel.resolvedMinimalTree.genericTags.
 */
const getDerivedProps = props => {
  const {
    categoryGradeBoundaryValues,
    assessmentFields,
    subjectType: propsSubjectType,
    subjectLevelGenericTags,
    mode,
  } = props;

  if (
    _.isEmpty(propsSubjectType) &&
    _.isEmpty(categoryGradeBoundaryValues) &&
    mode !== "view"
  ) {
    const subjects = getResolvedMinimalTreeSet({
      fields: assessmentFields,
      uid: "subjects",
    });

    const subjectType = _.get(subjects, "0.type", "");
    const subjectLabel = _.get(subjects, "0.name");

    // V2: pass levelTags (from assignment subjectLevel field) to init template
    const derivedCategoryGradeBoundaryValues = getGradeBoundaryInitialValues({
      subjectType,
      levelTags: subjectLevelGenericTags,
    });

    return {
      subjectType,
      subjectLabel,
      categoryGradeBoundaryValues: derivedCategoryGradeBoundaryValues,
    };
  }

  return {};
};

// ─── mapStateToProps ──────────────────────────────────────────────────────────

const mapStateToProps = (state, ownProps) => {
  const {
    t,
    assessmentFields = [],
    category = {},
    academicTermId,
    assessmentTool,
    mode,
    assessmentTools = [],
    portalType,
  } = ownProps;

  const title =
    _.find(assessmentFields, field => {
      return field?.uid === "title";
    })?.value || t("common:untitled");

  const organizationId = state.login.userInfo.org_id;

  const _assessmentTools = assessmentTools?.edges
    ? assessmentTools?.edges
    : assessmentTools;

  const { node: { assessmentToolItem, assessmentToolType } = {} } =
    _.find(_assessmentTools, ({ node }) => {
      return getIsIBGradeScaleTool({
        assessmentToolType: node?.assessmentToolType,
        checkForManual: false,
      });
    }) || {};

  const isToolCreated = mode === "edit";
  const isEditable = mode === "create" || isToolCreated;

  const toolData = assessmentTool || assessmentToolItem || {};

  // V2: assessmentToolItem for new grade scale tools is GradeScaleEntityDetail,
  // which uses gradeScaleValues instead of academicCriteriaValues. Transform it
  // into the academicCriteriaValues shape that the rest of the component expects.
  let { academicCriteriaValues = [] } = toolData;
  if (_.isEmpty(academicCriteriaValues)) {
    const gradeScaleValues = _.get(toolData, "gradeScaleValues", []);
    if (!_.isEmpty(gradeScaleValues)) {
      academicCriteriaValues = _.map(gradeScaleValues, gsv => ({
        id: gsv.id,
        label: gsv.label,
        abbreviation: gsv.abbreviation,
        scoreValues: _.map(_.get(gsv, "gradeScaleValueScores", []), score => ({
          id: score.id,
          minValue: score.minValue,
          maxValue: score.maxValue,
          genericTag: {
            id: _.get(score, "subjectLevel.id", null),
            label: _.get(score, "subjectLevel.label", "SL"),
          },
        })),
      }));
    }
  }

  const { id: categoryId, label: categoryLabel } = category || {};

  const curriculumProgramId = _.get(
    state,
    "platform.currentCurriculumProgram.id"
  );

  const subjects = _.find(assessmentFields, field => {
    return _.get(field, "uid") === "subjects";
  });

  const subjectId = !_.isEmpty(subjects)
    ? _.first(subjects?.value)
    : _.get(ownProps, "subjects.0.id") || _.get(ownProps, "subjectId");

  let courseId = _.get(state, "teacher.selected_class.selected_course", "");
  courseId = !_.isEmpty(courseId) ? courseId : ownProps?.selectedCourseId;

  const { maxScore } = getScoreDetailsMemoize({
    assessmentTools,
  });

  const academicYearId = getSelectedAcademicYearId(state);

  /**
   * V2 KEY CHANGE: subjectLevelGenericTags now comes from the assignment's
   * subjectLevel field (resolvedMinimalTree.genericTags). This contains exactly
   * the levels tagged on this assignment — e.g. ["SL", "AP"] — not all course
   * levels. These become the grade scale table columns.
   */
  const subjectLevelField = _.find(assessmentFields, { uid: "subjectLevel" });
  let subjectLevelGenericTags = _.get(
    subjectLevelField,
    "resolvedMinimalTree.genericTags",
    []
  );

  // Fallback: some views (e.g. AllSubmissions) use a GraphQL fragment that does
  // not include resolvedMinimalTree on allFields, so genericTags is empty.
  // In those cases derive the level tags from the existing academicCriteriaValues
  // scoreValues, which always carry the subjectLevel genericTag data.
  if (
    _.isEmpty(subjectLevelGenericTags) &&
    !_.isEmpty(academicCriteriaValues)
  ) {
    const scoreValues = _.get(
      _.first(academicCriteriaValues),
      "scoreValues",
      []
    );
    const tagsFromScores = _.filter(
      _.map(scoreValues, sv => _.get(sv, "genericTag")),
      tag => !_.isNil(_.get(tag, "id"))
    );
    if (!_.isEmpty(tagsFromScores)) {
      subjectLevelGenericTags = _.uniqBy(tagsFromScores, "id");
    }
  }

  return {
    isData: true,
    isLoading: false,
    subjectId,
    categoryId,
    academicTermId,
    courseId,
    academicCriteriaValues,
    isEditable,
    isToolCreated,
    categoryLabel,
    assessmentTool: assessmentTool || assessmentToolItem,
    title,
    maxScore,
    assessmentToolType,
    organizationId,
    curriculumProgramId,
    academicYearId,
    subjectLevelGenericTags,
    learningCourseId:
      _.get(state, "planner.unitPlanData.learningCourseId", null) ||
      getSelectedLearningCourseId(state) ||
      _.get(ownProps, "learningCourseId"),
    isLearningCoursesEnabled: getIsLearningCoursesEnabled(),
  };
};

const mapActionCreators = {
  createAssessmentTool,
  updateCategoryGradeBoundary,
  setToastMsg,
  removeAssessmentTool,
  updateQueryParameters,
};

export default compose(
  I18nHOC({ resource: ["common", "academicSetup"] }),
  connect(mapStateToProps, mapActionCreators),
  graphql(getCourseSubjectFlatVariantsLevelQuery, {
    name: "getCourseSubjectFlatVariantsLevel",
    skip: ({ courseId, isLearningCoursesEnabled }) =>
      !courseId || isLearningCoursesEnabled,
    options: ({ courseId, portalType, academicYearId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: courseId,
        gradeFilters: { academicYearId },
        portalType,
      },
    }),
    props({
      getCourseSubjectFlatVariantsLevel,
      ownProps: {
        isData,
        isLoading,
        subjectLevelGenericTags, // V2: already in ownProps from connect/mapStateToProps
      },
    }) {
      const courseData = getSubjectFlatVariantsLevelFromCache(
        getCourseSubjectFlatVariantsLevel.variables
      );

      const subjects = _.get(courseData, "subjects", []);
      const gradeId = _.get(courseData, "grade.id");

      const subjectType = _.get(subjects, "0.type");
      const subjectLabel = _.get(subjects, "0.name");

      const networkStatus = _.get(
        getCourseSubjectFlatVariantsLevel,
        "networkStatus"
      );

      // V2: use levelTags from the assignment's subjectLevel field (already in ownProps)
      const categoryGradeBoundaryValues = getGradeBoundaryInitialValues({
        subjectType,
        levelTags: subjectLevelGenericTags,
      });

      return {
        isData: !_.isEmpty(courseData) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        // V2: subjectLevelGenericTags NOT returned here; comes from mapStateToProps
        subjectType,
        categoryGradeBoundaryValues,
        gradeId,
        subjectLabel,
      };
    },
  }),
  graphql(getLearningCourseSubjectFlatVariantsLevelQuery, {
    name: "getLearningCourseSubjectFlatVariantsLevel",
    skip: ({ learningCourseId, isLearningCoursesEnabled }) =>
      !learningCourseId || !isLearningCoursesEnabled,
    options: ({ learningCourseId, portalType }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: learningCourseId,
        portalType,
      },
    }),
    props({
      getLearningCourseSubjectFlatVariantsLevel,
      ownProps: {
        isData,
        isLoading,
        subjectLevelGenericTags, // V2: already in ownProps from connect/mapStateToProps
      },
    }) {
      const courseData = _.get(
        getLearningCourseSubjectFlatVariantsLevel,
        "node",
        {}
      );

      const subjects = _.get(courseData, "subjects", []);
      const gradeId = _.get(courseData, "grades.0.id");

      const subjectType = _.get(subjects, "0.type");
      const subjectLabel = _.get(subjects, "0.name");

      const networkStatus = _.get(
        getLearningCourseSubjectFlatVariantsLevel,
        "networkStatus"
      );

      // V2: use levelTags from the assignment's subjectLevel field (already in ownProps)
      const categoryGradeBoundaryValues = getGradeBoundaryInitialValues({
        subjectType,
        levelTags: subjectLevelGenericTags,
      });

      return {
        isData: !_.isEmpty(courseData) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        // V2: subjectLevelGenericTags NOT returned here; comes from mapStateToProps
        subjectType,
        categoryGradeBoundaryValues,
        gradeId,
        subjectLabel,
      };
    },
  }),
  // V2: category grade boundary — Subject path
  graphql(getSubjectCategoryGradeScaleQuery, {
    name: "getSubjectCategoryGradeScale",
    skip: ({ subjectId, categoryId, mode }) =>
      !subjectId || !categoryId || mode === "view",
    options: ({ subjectId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: subjectId,
        filters: { type: "SCORE_BASED" },
      },
    }),
    props({
      getSubjectCategoryGradeScale,
      ownProps: { categoryId, academicTermId, isData, isLoading },
    }) {
      const configs = _.get(
        getSubjectCategoryGradeScale,
        "node.gradingConfigurations",
        []
      );
      const {
        gradeScale,
        hasCategoryGradeBoundary,
      } = getCategoryGradeScaleFromConfig({
        gradingConfigurations: configs,
        categoryId,
        academicTermId,
      });
      return {
        categoryGradeBoundaries: transformGradeScaleToGradeBoundaries({
          gradeScale,
        }),
        hasCategoryGradeBoundary,
        categoryPointSetId: null,
        isData: !_.isEmpty(configs) && isData,
        isLoading:
          _.includes([1, 2], getSubjectCategoryGradeScale.networkStatus) ||
          isLoading,
      };
    },
  }),
  // V2: term grade boundary
  graphql(getSubjectTermGradeScaleQuery, {
    name: "getSubjectTermGradeScale",
    skip: ({ subjectId, academicTermId, gradeId, mode }) =>
      mode === "view" || !subjectId || !academicTermId || !gradeId,
    options: ({ subjectId, gradeId }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: subjectId, filters: { gradeIds: [gradeId] } },
    }),
    props({
      getSubjectTermGradeScale,
      ownProps: {
        academicTermId,
        subjectLevelGenericTags, // V2: used for filtering term boundaries to tagged levels
        isData,
        isLoading,
      },
    }) {
      const gradeScales = _.get(
        getSubjectTermGradeScale,
        "node.gradeScales",
        []
      );
      const {
        termEntityDetail,
        hasTermGradeBoundary,
      } = getTermGradeScaleEntityDetail({ gradeScales, academicTermId });

      return {
        termGradeBoundaries: getFilteredGradeBoundaries({
          gradeBoundary: transformTermEntityDetailToGradeBoundaries({
            termEntityDetail,
          }),
          subjectLevelGenericTags,
        }),
        hasTermGradeBoundary,
        termPointSetId: null,
        isData: isData,
        isLoading:
          _.includes([1, 2], getSubjectTermGradeScale.networkStatus) ||
          isLoading,
      };
    },
  }),
  withRouter,
  withDerivedProps(getDerivedProps)
)(AutomaticIBScaleToolV2);
